import appStore from '@stores/appStore'
import dashboardStore from '@stores/dashboard'
import eventBus, { EVENT_TYPE } from '@utils/eventBus'
import dayjs from 'dayjs'

import { apiService } from '../services/api'
import type {
  CardTemplate,
  CarouselResponseTemplate,
  IntentDefinition,
  IntentHandler,
  ListResponseTemplate,
  QuickRepliesTemplate,
  ResponseTemplate,
  SessionAvailabilityResponse,
  SpecialistInfo,
} from '../types/intent'
import { SESSION_STATUS_ENUM } from '@utils/constants'

const greetingHandler: IntentHandler = async () => {
  const timeOfDay = new Date().getHours()
  let greeting = ''

  if (timeOfDay < 12) {
    greeting = 'Good morning!'
  } else if (timeOfDay < 18) {
    greeting = 'Good afternoon!'
  } else {
    greeting = 'Good evening!'
  }

  const quickRepliesTemplate: QuickRepliesTemplate = {
    text: `${greeting} I'm Kawaconn's AI assistant. I can help you with: \n
🔹 Check Specialist Availability
What can I help you with today?`,
    replies: [
      {
        title: '🔍 Check Specialist Availability',
        payload: 'Check Specialist Availability',
      },
      {
        title: '📅 Book Appointment',
        payload: 'I want to book an appointment',
      },
    ],
  }

  const responseTemplate: ResponseTemplate = {
    type: 'quick_replies',
    content: quickRepliesTemplate,
  }

  return {
    response: '',
    template: responseTemplate,
  }
}

const goodbyeHandler: IntentHandler = async () => {
  const responses = [
    'Thank you for choosing Kawaconn! Have a great day! 👋',
    'Goodbye! Feel free to come back anytime if you need assistance! 🌟',
    "Take care! We're always here to help you! 💙",
    "See you later! Don't hesitate to reach out if you need anything! 👋",
  ]

  const selectedResponse =
    responses[Math.floor(Math.random() * responses.length)]

  const quickRepliesTemplate: QuickRepliesTemplate = {
    text: 'Before you go, here are some quick actions:',
    replies: [
      {
        title: '🆕 Start Over',
        payload: 'Hello',
      },
      {
        title: '📞 Contact Support',
        payload: 'Support',
      },
    ],
  }

  return {
    response: selectedResponse,
    template: {
      type: 'quick_replies',
      content: quickRepliesTemplate,
    },
  }
}

const sessionAvailableHandler: IntentHandler = async (
  entities,
  _context,
  _userMessage
) => {
  try {
    const doctorName = entities.specialist_name?.value
    const doctorEmail = entities.specialist_email?.value
    const specialty = entities.specialist_specialty?.value

    // Call API to get session information
    const searchParams = {
      specialistName: doctorName,
      specialistEmail: doctorEmail,
      specialty: specialty,
      accreditation: entities.specialist_accreditation?.value,
      fromDate: entities.from_date?.value,
      toDate: entities.to_date?.value,
      relativeDate: entities.relative_date?.value,
      date: entities.date?.value,
      isVideoAppointment: entities.is_video_appointment?.value,
      isBooked: false,
      postcode: entities.postcode?.value,
      state: entities.state?.value,
    }

    if (searchParams.date) {
      const dayOfWeekTerms = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ]
      // Check if date is a day of the week
      if (dayOfWeekTerms.includes(searchParams.date.toLowerCase().trim())) {
        // Convert to next occurrence of that day
        const today = dayjs()
        const targetDay =
          dayOfWeekTerms.indexOf(searchParams.date.toLowerCase().trim()) + 1

        let daysToAdd = targetDay - today.day()
        if (daysToAdd <= 0) {
          daysToAdd += 7
        } // Ensure it's the next occurrence
        const nextDate = today.add(daysToAdd, 'day')
        searchParams.fromDate = nextDate.startOf('day').toISOString()
        searchParams.toDate = nextDate.endOf('day').toISOString()
      } else {
        try {
          // if date in MM/YYYY or MMMM YYYY format, set fromDate and toDate to first and last day of month
          const monthDate = dayjs(
            searchParams.date,
            ['MM/YYYY', 'MMMM YYYY'],
            true
          )

          if (monthDate.isValid()) {
            searchParams.fromDate = monthDate.startOf('month').toISOString()
            searchParams.toDate = monthDate
              .endOf('month')
              .endOf('day')
              .toISOString()
          } else {
            searchParams.fromDate = dayjs(searchParams.date, 'DD/MM/YYYY')
              .startOf('day')
              .toISOString()

            searchParams.toDate = dayjs(searchParams.date, 'DD/MM/YYYY')
              .endOf('day')
              .toISOString()
          }
        } catch (error) {}
      }
    } else {
      if (searchParams.fromDate) {
        const monthDate = dayjs(
          searchParams.fromDate,
          ['MM/YYYY', 'MMMM YYYY'],
          true
        )
        if (monthDate.isValid()) {
          searchParams.fromDate = monthDate.startOf('month').toISOString()
        } else {
          searchParams.fromDate = dayjs(
            searchParams.fromDate,
            'DD/MM/YYYY'
          ).toISOString()
        }
      }
      if (searchParams.toDate) {
        const monthDate = dayjs(
          searchParams.toDate,
          ['MM/YYYY', 'MMMM YYYY'],
          true
        )
        if (monthDate.isValid()) {
          searchParams.toDate = monthDate
            .endOf('month')
            .endOf('day')
            .toISOString()
        } else {
          searchParams.toDate = dayjs(searchParams.toDate, ['DD/MM/YYYY'])
            .endOf('day')
            .toISOString()
        }
      }
    }

    // Valid at least one params exists from searchParams
    const hasSearchParams = Object.values(searchParams).some(param => !!param)

    // Check if no specialist information provided
    if (!hasSearchParams) {
      // Get examples from session_availability intent
      return getDefaultResponse(entities)
    }

    let specialists: SpecialistInfo[] = []
    let specialist: SpecialistInfo | undefined
    // If no doctor_email but doctor_name is provided, search for specialists first
    if (!searchParams.specialistEmail && searchParams.specialistName) {
      try {
        const resp = await apiService.findSpecialistByName(
          searchParams.specialistName
        )

        specialists = resp?.data ?? []

        // No specialists found
        if (specialists.length == 0) {
          return {
            response:
              `😔 **No valid specialists found**\n\n` +
              `No specialists found with the name "${doctorName}".\n\n` +
              `Please:\n` +
              `• Check the spelling of the doctor's name\n` +
              `• Try using the doctor's email instead`,
          }
        }

        // Exactly one specialist found - use their email
        if (specialists.length === 1) {
          searchParams.specialistEmail = specialists[0].businessEmail
          specialist = specialists[0]
        }

        // Check if this is a specialist selection response
        if (specialists.length > 1) {
          // Create specialist selection list
          const specialistItems = specialists.map(
            (specialist: SpecialistInfo) => ({
              title: `👨‍⚕️ ${specialist.fullName}`,
              subtitle:
                `📧 ${specialist.businessEmail}\n` +
                `🏥 ${specialist.specialty}\n`,
              actions: [
                {
                  type: 'postback' as const,
                  label: '🔍 Check Availability',
                  value: `Check availability for ${specialist.businessEmail}`,
                  style: 'primary' as const,
                },
              ],
              data: {
                specialist_id: specialist.id,
                specialist_email: specialist.businessEmail,
                specialist_name: specialist.fullName,
              },
            })
          )

          const listTemplate: ListResponseTemplate = {
            type: 'list',
            content: {
              header: `🔍 **Multiple specialists found named "${doctorName}"**`,
              items: specialistItems,
              footer:
                'Please select the specific specialist to check their availability.',
            },
          }

          return {
            response: '',
            template: listTemplate,
            data: {
              type: 'specialist_selection',
              specialists: specialists,
              search_name: doctorName,
            },
          }
        }
      } catch (error) {
        return {
          response: 'Error searching for specialists',
        }
      }
    }

    const response = await apiService.getSessionAvailability({
      specialistEmail: searchParams.specialistEmail,
      specialty: searchParams.specialty,
      accreditation: searchParams.accreditation,
      fromDate: searchParams.fromDate,
      toDate: searchParams.toDate,
      relativeDate: searchParams.relativeDate,
      isVideoAppointment: searchParams.isVideoAppointment,
      postcode: searchParams.postcode,
      state: searchParams.state,
    })

    // Handle normal session availability response
    const availableResponse = response?.data as SessionAvailabilityResponse

    // Build entity match summary if any entities are present
    const matchedEntities: string[] = []
    if (doctorName) {
      matchedEntities.push(`• Name: ${doctorName}`)
    }
    if (doctorEmail) {
      matchedEntities.push(`• Email: ${doctorEmail}`)
    }
    if (specialty) {
      matchedEntities.push(`• Specialty: ${specialty}`)
    }
    if (searchParams.accreditation) {
      matchedEntities.push(`• Accreditation: ${searchParams.accreditation}`)
    }

    if (searchParams.fromDate) {
      matchedEntities.push(
        `• From: ${dayjs(searchParams.fromDate).format('DD MMM YYYY')}`
      )
    }

    if (searchParams.toDate) {
      matchedEntities.push(
        `• To: ${dayjs(searchParams.toDate).format('DD MMM YYYY')}`
      )
    }
    if (searchParams.relativeDate) {
      matchedEntities.push(`• Relative Date: ${searchParams.relativeDate}`)
    }

    if (searchParams.state) {
      matchedEntities.push(`• State: ${searchParams.state}`)
    }

    if (searchParams.isVideoAppointment === true) {
      matchedEntities.push(`• Session Type: Telehealth Appointment`)
    }
    if (searchParams.isVideoAppointment === false) {
      matchedEntities.push(`• Session Type: In-Person Appointment`)
    }

    const entitySummary = matchedEntities.length
      ? `🔎 **Matched search criteria:**\n${matchedEntities.join('\n')}\n\n`
      : ''

    if (availableResponse.data?.sessions?.length == 0) {
      return {
        response:
          entitySummary +
          `😔 **No available slots found**\n` +
          `\nPlease:\n` +
          `• Check doctor name/email again\n` +
          `• Try searching with different specialty\n` +
          `💡 **Suggestion:** Try searching "all cardiology doctors" for more options!`,
      }
    }

    // Format session information
    // const doctorInfo = availableResponse.doctor_info;
    let sessions = availableResponse.data?.sessions?.slice(0, 5) // Only show first 5 results
    specialist = availableResponse.data?.specialist
    const totalCount = availableResponse.totalCount

    // Create session cards for carousel template
    const sessionCards: CardTemplate[] = sessions.map(session => {
      const dateFrom = new Date(session.dateFrom)
      const dateTo = new Date(session.dateTo)

      // calculate duration in minutes
      const duration = Math.round(
        (dateTo.getTime() - dateFrom.getTime()) / 60000
      )

      const appointmentDateText = appStore.formatDateTime(
        session.dateFrom,
        session.timeZone
      )
      const specialist = session.specialist
      const specialistDisplayName = `${
        specialist?.title ? specialist.title + ' ' : ''
      }${specialist?.fullName ?? ''}`

      return {
        title: `📅 ${appointmentDateText} (${session.timeZone})`,
        subtitle:
          // `🗓️ Appointment Time: ${appointmentDateText} (${session.timeZone})\n` +
          `⏱️ Specialist: ${specialistDisplayName}\n` +
          `⏱️ Duration: ${duration} minutes\n` +
          `📍 Location: ${session.centre?.name ?? ''}\n` +
          `💻 Session Type: ${
            session.isVideoAppointment ? 'Telehealth' : 'In-Person'
          }` +
          (session.reservedForCompanies
            ? `\n🏢 Reserved Companies: ${session.reservedForCompanies
                .map(i => i.displayName)
                .join(', ')}`
            : ''),

        actions:
          session.sessionStatus === 'available'
            ? [
                {
                  type: 'postback' as const,
                  label: '📅 Book',
                  value: `BOOK_SESSION_${session.id}`,
                  style: 'primary' as const,
                },
                {
                  type: 'link' as const,
                  label: 'ℹ️ Book Now',
                  value: `#session-details-${session.id}`,
                  style: 'secondary' as const,
                  handleClick: () => {
                    dashboardStore.open('/view/calendar' + session.id, {
                      callback: () => {
                        eventBus.emit(
                          EVENT_TYPE.OPEN_SESSION_DETAIL,
                          session.id
                        )
                      },
                    })
                  },
                },
              ]
            : [
                {
                  type: 'link' as const,
                  label: 'ℹ️ Book Now',
                  value: `#session-details-${session.id}`,
                  style: 'secondary' as const,
                  handleClick: () => {
                    dashboardStore.open('/view/calendar')

                    setTimeout(() => {
                      eventBus.emit(EVENT_TYPE.OPEN_SESSION_DETAIL, session.id)
                    }, 200)
                  },
                },
              ],
        data: {
          session_id: session.id,
          status: session.sessionStatus,
          doctor_email: searchParams.specialistEmail,
        },
      }
    })

    const responseData: any = availableResponse.data ?? {}

    const openCalendarWithFilter = () => {
      const queryParams = {
        doctorId: specialist?.id ?? undefined,
        start: searchParams.fromDate ?? undefined,
        end: searchParams.toDate ?? undefined,
        specialtyIds: responseData.specialtyId ?? undefined,
        accreditationIds: responseData.accreditationId ?? undefined,
        stateId: responseData.cityId ?? undefined,
        countryId: responseData.countryId ?? undefined,
        sessionStatuses: ['Available'],
        isVideoAppointment: searchParams.isVideoAppointment ?? undefined,
      }

      dashboardStore.close('/view/calendar')

      setTimeout(() => {
        // URLSearchParams should ignore undefined values

        const filteredParams = Object.fromEntries(
          Object.entries(queryParams).filter(([_, v]) => v !== undefined)
        )

        dashboardStore.open(
          '/view/calendar?' + new URLSearchParams(filteredParams).toString()
        )
      }, 500)
    }
    const responseText = (
      <div>
        <div>
          {entitySummary +
            `📊 **Found ${totalCount} available slots**\n` +
            `Here are the 5 nearest available slots:`}
        </div>
        {sessions.length > 0 && (
          <a
            href="#"
            className="ml-2 text-blue-600 underline block"
            onClick={e => {
              e.preventDefault()
              openCalendarWithFilter()
            }}
          >
            View All
          </a>
        )}
      </div>
    )

    // Carousel template to display session cards
    const carouselTemplate: CarouselResponseTemplate = {
      type: 'carousel',
      content: {
        headerText: `📅 **Available Slots for ${specialist?.fullName}**`,
        items: sessionCards,
      },
    }

    return {
      response: responseText,
      template: carouselTemplate,
      data: {
        doctor: specialist,
        sessions: availableResponse,
        total_count: totalCount,
        search_params: searchParams,
        // quick_replies: quickRepliesTemplate,
      },
    }
  } catch (error) {
    console.error('Session availability error:', error)
    return {
      response:
        `😔 **An error occurred while finding availability**\n\n` +
        `Please try again or contact our staff for more support.:\n` +
        `We apologize for the inconvenience! 🙏`,
    }
  }
}

const supportHandler: IntentHandler = async () => {
  return {
    response:
      `🛠️ **Technical Support**\n\n` +
      `I'm here to help with any technical issues!\n\n` +
      `**Contact Information:**\n` +
      `📧 Email: support@kawaconn.com\n` +
      `📞 Phone: +84 123 456 789\n` +
      `⏰ Business Hours: 9:00 AM - 6:00 PM (Monday - Friday)\n\n` +
      `**Common Issues:**\n` +
      `• Login problems\n` +
      `• Technical difficulties\n` +
      `• Account access\n` +
      `• System errors\n\n` +
      `Please describe your issue and we'll get back to you as soon as possible! 🚀`,
  }
}

const otherHandler: IntentHandler = async () => {
  return {
    response:
      `🤔 **I didn't quite understand that**\n\n` +
      `I can help you with:\n\n` +
      `📅 **Check Specialist Availability**\n` +
      `• Find doctor schedules\n` +
      `• Book appointments\n` +
      `• View available time slots\n\n` +
      `🛠️ **Technical Support**\n` +
      `• Troubleshoot issues\n` +
      `• Get technical help\n` +
      `• Contact support team\n\n` +
      `Could you please rephrase your request or choose from the options above? 😊`,
  }
}

export const DefaultIntents: Record<string, IntentDefinition> = {
  greeting: {
    name: 'greeting',
    description: 'Handle greetings and welcome messages',
    examples: [
      'Hello',
      'Hi',
      'Good morning',
      'Hey there',
      'Good afternoon',
      'Good evening',
      'Greetings',
    ],
    entities: [],
    confidence_threshold: 0.7,
    handler: greetingHandler,
  },

  goodbye: {
    name: 'goodbye',
    description: 'Handle farewell messages',
    examples: [
      'Goodbye',
      'Bye',
      'See you later',
      'Thanks, bye',
      'Have a good day',
      'Farewell',
      'Take care',
    ],
    entities: [],
    confidence_threshold: 0.7,
    handler: goodbyeHandler,
  },

  session_availability: {
    name: 'session_availability',
    description: 'Check doctor session availability.',
    examples: [
      "Check Dr Peter's availability",
      'Find available slots for peter.smith@hospital.com',

      // Examples with date_from, date_to, and specialist_accreditation
      'Find available slots for Dr Peter from 10/06/2024 to 15/06/2024',
      'Find telehealth sessions for Dr Lucy in July 2024',
      'Check Dr Lucy in October 2025',
      'Check Dr Lucy in 10/2025',
      'Check Dr Peter availability next week',
      'Find specialists with American Board accreditation',
      'Check availability for Dr Lucy with "FRCP" accreditation',
      'Find cardiology specialists accredited by American Board',
      'Show available sessions for Dr Peter from tomorrow to Friday',
      'Show available sessions from 01/09/2025 to 10/09/2025',
    ],
    entities: [
      {
        name: 'specialist_name',
        type: 'string',
        required: false,
        description:
          'Extract the full name of the specialist including any title (e.g., Dr., Prof.) and specialty if directly attached to the name (e.g., “Dr Lucy Psychiatrist” should be extracted as the full specialist_name).',
      },
      {
        name: 'specialist_email',
        type: 'email',
        required: false,
        description: "Specialist's email",
      },
      {
        name: 'specialist_specialty',
        type: 'string',
        required: false,
        description:
          "Specialist's specialty. Extract specialty value if present.",
      },
      {
        name: 'specialist_accreditation',
        type: 'string',
        required: false,
        description:
          "Specialist's accreditation. Extract accreditation value if present.",
      },
      {
        name: 'from_date',
        type: 'date',
        required: false,
        description:
          'Start date for searching available sessions. Date can be in DD/MM/YYYY, MM/YYYY or MMMM YYYY format',
      },
      {
        name: 'to_date',
        type: 'date',
        required: false,
        description:
          'End date for searching available sessions. Date can be in DD/MM/YYYY, MM/YYYY or MMMM YYYY format',
      },
      {
        name: 'date',
        type: 'date',
        required: false,
        description:
          'Date for searching available sessions. If input is a specific date in format DD/MM/YYYY or day of week (Monday, Tuesday, Wednesday, ...).',
      },
      {
        name: 'relative_date',
        type: 'string',
        required: false,
        description:
          'Relative date for searching available sessions. (e.g., "next week", "tomorrow", "today", "next month") convert value into one of these items NextWeek, Tomorrow, Today, NextMonth, NextYear, ThisWeek, ThisMonth, ThisYear, Next3Days, Next7Days.',
      },
      {
        name: 'is_video_appointment',
        type: 'boolean',
        required: false,
        description:
          'Indicates if the appointment is a video appointment. Defaults to null. If query mentions "video" or "telehealth", set to true. If query mentions "in-person", set to false.',
      },
      {
        name: 'postcode',
        type: 'string',
        required: false,
        description: 'Postcode for searching available sessions.',
      },
      {
        name: 'state',
        type: 'string',
        required: false,
        description:
          'State for searching available sessions. If state is mentioned',
      },
    ],
    rules: [
      {
        title: 'Date Range Rules',
        content: `   - If the query mentions relative time expressions (e.g., next week, next month, tomorrow, today), put then into relative_date\n`,
      },
      {
        title: 'Date Rules',
        content: `   - If the query mentions day of the week  (e.g., Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday), then capture it as a specific date in the format "DD/MM/YYYY".\n`,
      },
    ],
    confidence_threshold: 0.6,
    handler: sessionAvailableHandler,
  },

  // support: {
  //   name: 'support',
  //   description: 'Handle technical support requests',
  //   examples: [
  //     'I need help',
  //     'Technical support',
  //     'Support',
  //     'Help me',
  //     'Contact support',
  //     'I have a problem',
  //     'Technical issue',
  //     'Something is wrong',
  //   ],
  //   entities: [],
  //   confidence_threshold: 0.7,
  //   handler: supportHandler,
  // },

  other: {
    name: 'other',
    description: 'Fallback for unrecognized intents',
    examples: ['Random text', 'Something unknown', "I don't know what to say"],
    entities: [],
    confidence_threshold: 0.3,
    handler: otherHandler,
  },
}

export const getDefaultResponse = (entities: any) => {
  const sessionAvailabilityIntent = DefaultIntents.session_availability
  const examples = sessionAvailabilityIntent.examples || []
  const displayExamples = examples.slice(0, 8) // Show first 8 examples

  // Format examples for display
  // const examplesList = displayExamples.map(example => `• ${example}`).join('\n')

  return {
    response: (
      <div>
        <h3>
          <b>📅 Find doctor availability</b>
        </h3>
        <p>
          To find available slots, please provide relevant information such as:
        </p>
        <ul>
          <li>- Doctor name or doctor email</li>
          <li>- Telehealth or In-person appointment</li>
          <li>- Appointment date and time</li>
          <li>- Suburb or postcode</li>
          <li>- Specialty</li>
          <li>- Accreditation</li>
        </ul>
        <p>
          <b>Suggested message format:</b>
        </p>
        <blockquote>
          [Find/Check] [Dr Name/Email] [with Accreditation "value"] [with
          Specialty "value"] [at Suburb/Postcode/State "value"] [for
          Telehealth/In-person] [on Date/From Date - To Date/Relative Date]
        </blockquote>
        <p>
          <b>Examples:</b>
        </p>
        <ul>
          {displayExamples.map((example, index) => (
            <li key={index}>- {example}</li>
          ))}
        </ul>
        <p>Please provide more information so I can assist you better.</p>
      </div>
    ),
    actions: [
      {
        type: 'form' as const,
        payload: {
          fields: [
            'specialist_name',
            'specialist_email',
            'specialist_specialty',
            'specialist_accreditation',
          ],
          current_data: entities,
        },
      },
    ],
  }
}
// Export intentRegistry from IntentRegistry
export { intentRegistry } from './IntentRegistry'
